import {
  AthleteTeamProfile,
  DEFAULT_ONE_REP_MAX_FORMULA,
  athleteDisplayName,
} from '@strength/domain';
import { DateTime } from 'luxon';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 } from 'uuid';
import { trpc } from '../../../../trpc/react';
import { useViewer } from '../../../context/viewer';
import { useToast } from '../../../hooks/useToast';
import { hydrateCoreExercises } from '../utils';
import { AthleteExerciseBenchmarkHistory } from './AthleteExerciseBenchmarkHistory';
import { AthleteKpiBenchmarkHistory } from './AthleteKpiBenchmarkHistory';
import { AthleteExerciseBenchmarkCell } from './BenchmarksDataTable';
import { BenchmarksDrawer, BenchmarksForm } from './BenchmarksDrawer';
import { BenchmarksTable } from './BenchmarksTable';
import {
  buildAthleteKpiBenchmarkRows,
  buildRecordKpiBenchmarkInput,
  type RecordAthleteKpiBenchmarkCell,
  shouldRenderKpiGrid,
} from './kpi-benchmarks-utils';

export const BenchmarksPage = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [selectedAthlete, setSelectedAthlete] =
    useState<AthleteTeamProfile | null>(null);
  // User's manual selection of grid (may not exist in current team's grids)
  const [userSelectedGridId, setUserSelectedGridId] = useState<string | ''>('');
  const [benchmarksDrawerOpen, setBenchmarksDrawerOpen] = useState(false);
  // the grid for edit in the benchmarks drawer, if one to edit
  const [gridIdForEdit, setGridIdForEdit] = useState<string | null>(null);
  const {
    team: { selectedTeam: teamId, teams },
    hasPermissions,
  } = useViewer();
  const canEditBenchmarks = hasPermissions('teams.benchmarks', 'edit');

  // Note: this is only used to get the one rep max formula
  const teamDetailResult = trpc.roster.teamDetail.useQuery(undefined, {
    staleTime: 10_000,
  });
  const oneRepMaxBenchmarkFormula =
    teamDetailResult.data?.settings?.oneRepMaxFormula;

  const benchmarkGridsResult = trpc.benchmarks.fetchGrids.useQuery(undefined, {
    staleTime: 10_000,
  });

  // Derive the effective selected grid ID:
  // - Use user's selection if it exists in current team's grids
  // - Otherwise, use first grid if available
  // - Otherwise, use empty string
  const selectedTabBenchmarkGridId = useMemo(() => {
    const grids = benchmarkGridsResult.data ?? [];

    // If user selected a grid that exists in current grids, use it
    if (userSelectedGridId && grids.some((g) => g.id === userSelectedGridId)) {
      return userSelectedGridId;
    }

    // Otherwise, use first grid if available
    return grids[0]?.id ?? '';
  }, [benchmarkGridsResult.data, userSelectedGridId]);

  const selectedGrid = benchmarkGridsResult.data?.find(
    (grid) => grid.id === selectedTabBenchmarkGridId,
  );
  // KPI grids only render/record the KPI path when the flag is on; otherwise
  // (and for rep grids) the exercise-benchmark path renders exactly as before.
  const isKpiGrid = shouldRenderKpiGrid(selectedGrid);

  const teamAthleteResult = trpc.roster.teamAthletes.useQuery(
    {},
    {
      staleTime: 10_000,
    },
  );
  const allExercisesResult = trpc.exerciseLibrary.exerciseSearch.useQuery(
    {
      teamId: teamId || undefined,
      searchTerm: '',
    },
    {
      staleTime: 10_000,
    },
  );
  const teamExerciseBenchmarksResult =
    trpc.athleteBenchmarks.teamExerciseBenchmarks.useQuery(
      {
        benchmarkGridId: selectedTabBenchmarkGridId ?? '',
      },
      {
        staleTime: 10_000,
        enabled: !!selectedTabBenchmarkGridId && !isKpiGrid,
      },
    );

  // KPI grid data: metric columns + the current (latest) value per
  // (athlete, metric). Team-scoped queries; the team header is injected by the
  // tRPC client (same as `teamExerciseBenchmarks`).
  const kpiGridMetricsResult = trpc.benchmarks.kpiGridMetrics.useQuery(
    { gridId: selectedTabBenchmarkGridId },
    {
      staleTime: 10_000,
      enabled: !!selectedTabBenchmarkGridId && isKpiGrid,
    },
  );
  const teamKpiBenchmarksResult =
    trpc.athleteKpiBenchmarks.teamKpiBenchmarks.useQuery(
      { benchmarkGridId: selectedTabBenchmarkGridId },
      {
        staleTime: 10_000,
        enabled: !!selectedTabBenchmarkGridId && isKpiGrid,
      },
    );

  const updateAthleteExerciseBenchmark =
    trpc.athleteBenchmarks.manualAddAthleteExerciseBenchmark.useMutation({
      onSuccess: () => {},
    });

  const recordAthleteKpiBenchmark =
    trpc.athleteKpiBenchmarks.recordAthleteKpiBenchmark.useMutation();

  const createBenchmarkGrid = trpc.benchmarks.createBenchmarkGrid.useMutation({
    onSuccess: () => {
      toast({
        title: t('success'),
        description: t('benchmarkMetricAdded'),
        severity: 'success',
      });
      benchmarkGridsResult.refetch();
    },
  });

  const editBenchmarkGrid = trpc.benchmarks.editBenchmarkGrid.useMutation({
    onSuccess: () => {
      toast({
        title: t('success'),
        description: t('benchmarkMetricUpdated'),
        severity: 'success',
      });
      benchmarkGridsResult.refetch();
      // A KPI grid edit can add/remove metric columns; `kpiGridMetrics` is its
      // own query (not covered by `benchmarkGridsResult`), so without this the
      // grid keeps showing a removed metric's column until the page reloads.
      // Only relevant for KPI grids — `refetch()` bypasses `enabled` and would
      // otherwise fire this query needlessly on rep-grid edits too.
      if (isKpiGrid) {
        kpiGridMetricsResult.refetch();
      }
    },
  });

  const deleteBenchmarkGrid = trpc.benchmarks.deleteBenchmarkGrid.useMutation({
    onSuccess: () => {
      toast({
        title: t('success'),
        description: t('benchmarkMetricDeleted'),
        severity: 'success',
      });
      benchmarkGridsResult.refetch();
      setUserSelectedGridId('');
    },
  });

  const { data, coreExercises, teamName } = useMemo(() => {
    const teamName = teams.find((t) => t.id === teamId)?.name;

    if (
      !allExercisesResult.data ||
      !teamAthleteResult.data ||
      !teamExerciseBenchmarksResult.data ||
      !benchmarkGridsResult.data
    ) {
      return {
        data: [],
        coreExercises: [],
        teamName,
      };
    }

    const selectedBenchmarkGrid = selectedTabBenchmarkGridId
      ? benchmarkGridsResult.data.find(
          (grid) => grid.id === selectedTabBenchmarkGridId,
        )
      : undefined;
    if (!selectedBenchmarkGrid) {
      return {
        data: [],
        coreExercises: [],
        teamName,
      };
    }

    const coreExercises = hydrateCoreExercises(
      selectedBenchmarkGrid.coreExercises,
      allExercisesResult.data?.records,
    );
    const athletes: AthleteTeamProfile[] = teamAthleteResult.data;
    const athleteExerciseBenchmarks = teamExerciseBenchmarksResult.data;

    const data = [
      ...athletes.map((athlete, index) => {
        const exerciseBenchmarksForAthlete = athleteExerciseBenchmarks.filter(
          (m) => m.profileId === athlete.id,
        );

        // Create an object with all core exercises initialized with null values
        const initialExerciseValues = Object.assign(
          {},
          ...coreExercises.map((exercise) => ({
            [exercise.id]: { value: null, id: exercise.id, unit: null },
          })),
        );

        // Populate with actual values where they exist
        const coreExerciseValues = Object.assign(
          initialExerciseValues,
          ...exerciseBenchmarksForAthlete.map(
            ({ libraryExerciseId, value, unit }) => {
              const match = coreExercises.find(
                (e) => e.id === libraryExerciseId,
              );

              if (!match) {
                return {};
              }

              return {
                [libraryExerciseId]: {
                  value,
                  id: libraryExerciseId,
                  unit,
                  benchmarkGridId: selectedBenchmarkGrid.id,
                },
              };
            },
          ),
        );

        return {
          id: athlete.id,
          index,
          profile: athleteDisplayName(athlete),
          athlete,
          ...coreExerciseValues,
        };
      }),
    ];

    return { data, coreExercises, teamName };
  }, [
    allExercisesResult.data,
    teamAthleteResult.data,
    teamExerciseBenchmarksResult.data,
    benchmarkGridsResult.data,
    selectedTabBenchmarkGridId,
    teams,
    teamId,
  ]);

  const kpiMetrics = kpiGridMetricsResult.data ?? [];
  const kpiData = useMemo(
    () =>
      buildAthleteKpiBenchmarkRows(
        teamAthleteResult.data ?? [],
        kpiGridMetricsResult.data ?? [],
        teamKpiBenchmarksResult.data ?? [],
      ),
    [
      teamAthleteResult.data,
      kpiGridMetricsResult.data,
      teamKpiBenchmarksResult.data,
    ],
  );

  const onSetAthleteId = (profileId: string) => {
    setSelectedAthlete(
      teamAthleteResult.data?.find((a) => a.id === profileId) ?? null,
    );
  };

  // Records a KPI value as a new dated entry (current time). On success the
  // grid query is refetched so the cell shows the new value as current, then
  // `onSuccess` restores cell focus. A server rejection (e.g. BAD_REQUEST for a
  // non-numeric value, or FORBIDDEN when the flag is off) surfaces both as an
  // accessible error toast (role="alert") and via `onError`, which reverts the
  // originating cell to its pre-edit value and shows its inline error.
  const onRecordKpiValueCell = useCallback(
    (
      cell: RecordAthleteKpiBenchmarkCell,
      onSuccess: () => void,
      onError: () => void,
    ) => {
      const date = DateTime.now().toISO();
      if (!date) {
        return;
      }
      recordAthleteKpiBenchmark.mutate(
        buildRecordKpiBenchmarkInput(cell, date),
        {
          onSuccess: () => {
            teamKpiBenchmarksResult.refetch().then(onSuccess);
          },
          onError: () => {
            onError();
            toast({
              title: t('error'),
              description: t('somethingWentWrong'),
              severity: 'error',
            });
          },
        },
      );
    },
    [recordAthleteKpiBenchmark, teamKpiBenchmarksResult, toast, t],
  );

  const onSaveBenchmarkValueCell = (
    cell: AthleteExerciseBenchmarkCell,
    onSuccess: () => void,
  ) => {
    const { profileId, value, unit, libraryExerciseId, benchmarkGridId } = cell;

    updateAthleteExerciseBenchmark.mutate(
      {
        profileId,
        snapshotId: v4(),
        currentId: v4(),
        date: DateTime.now().toISODate(),
        weight: value,
        unit,
        libraryExerciseId,
        formula: oneRepMaxBenchmarkFormula ?? DEFAULT_ONE_REP_MAX_FORMULA,
        benchmarkGridId,
      },
      {
        onSuccess: () => {
          teamExerciseBenchmarksResult.refetch().then(onSuccess);
        },
      },
    );
  };

  // Returns the mutation promise so the drawer can await success (to close) or
  // catch the router's duplicate-metric error and show it inline.
  const onCreateBenchmarkGrid = (benchmarksForm: BenchmarksForm) =>
    createBenchmarkGrid.mutateAsync(
      buildCreateBenchmarkGridInput(benchmarksForm),
    );

  const onEditBenchmarkGrid = (
    benchmarksForm: BenchmarksForm,
    gridId: string,
  ) =>
    editBenchmarkGrid.mutateAsync(
      buildEditBenchmarkGridInput(benchmarksForm, gridId),
    );

  const onDeleteBenchmarkGrid = (gridId: string) => {
    deleteBenchmarkGrid.mutate({ id: gridId });
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    if (newValue === 'add') {
      setBenchmarksDrawerOpen(true);
      return;
    }
    setUserSelectedGridId(newValue);
  };

  return (
    <div className="grow overflow-hidden h-full flex flex-col">
      <BenchmarksTable
        data={data}
        benchmarkGrid={selectedGrid}
        benchmarkGrids={benchmarkGridsResult.data ?? []}
        coreExercises={coreExercises}
        onSetAthleteId={onSetAthleteId}
        handleTabChange={handleTabChange}
        onSaveBenchmarkValueCell={onSaveBenchmarkValueCell}
        onOpenEditBenchmarkGrid={() => {
          setGridIdForEdit(selectedTabBenchmarkGridId);
          setBenchmarksDrawerOpen(true);
        }}
        isKpiGrid={isKpiGrid}
        kpiData={kpiData}
        kpiMetrics={kpiMetrics}
        onRecordKpiValueCell={onRecordKpiValueCell}
        canEdit={canEditBenchmarks}
      />
      {benchmarksDrawerOpen && (
        <BenchmarksDrawer
          open={benchmarksDrawerOpen}
          grid={
            gridIdForEdit
              ? benchmarkGridsResult.data?.find((g) => g.id === gridIdForEdit)
              : undefined
          }
          teamId={teamId}
          teamName={teamName ?? ''}
          allExercises={allExercisesResult.data?.records ?? []}
          onClose={() => {
            setBenchmarksDrawerOpen(false);
            setGridIdForEdit(null);
          }}
          onSubmit={(benchmarksForm, gridId) =>
            gridId
              ? onEditBenchmarkGrid(benchmarksForm, gridId)
              : onCreateBenchmarkGrid(benchmarksForm)
          }
          onDelete={(gridId) => onDeleteBenchmarkGrid(gridId)}
          canEdit={canEditBenchmarks}
        />
      )}
      {selectedAthlete &&
        (isKpiGrid ? (
          <AthleteKpiBenchmarkHistory
            open={true}
            onOpenChange={() => setSelectedAthlete(null)}
            athlete={selectedAthlete}
            metrics={kpiMetrics}
            benchmarkGridId={selectedTabBenchmarkGridId ?? ''}
          />
        ) : (
          <AthleteExerciseBenchmarkHistory
            open={true}
            onOpenChange={() => setSelectedAthlete(null)}
            athlete={selectedAthlete}
            benchmarkGridId={selectedTabBenchmarkGridId ?? ''}
            coreExercises={coreExercises}
          />
        ))}
    </div>
  );
};

/** Maps ordered core-exercise ids to the router's `{ sortOrder, libraryExerciseId }` shape. */
const mapCoreExercises = (coreExerciseIds: string[]) =>
  coreExerciseIds.map((id, i) => ({ sortOrder: i, libraryExerciseId: id }));

/**
 * Builds the `createBenchmarkGrid` input from the drawer form. KPI grids send
 * the selected metric ids and the server-forced defaults (repCount 0,
 * updateType none, no core exercises); rep grids send ordered core exercises
 * and no metrics.
 */
export const buildCreateBenchmarkGridInput = (
  benchmarksForm: BenchmarksForm,
) => {
  if (benchmarksForm.type === 'kpi') {
    return {
      name: benchmarksForm.name,
      type: 'kpi' as const,
      repCount: 0,
      updateType: 'none' as const,
      coreExercises: [],
      metricIds: benchmarksForm.metricIds,
    };
  }
  return {
    name: benchmarksForm.name,
    type: benchmarksForm.type,
    repCount: benchmarksForm.repCount,
    updateType: benchmarksForm.updateType,
    coreExercises: mapCoreExercises(benchmarksForm.coreExerciseIds),
    metricIds: [],
  };
};

/**
 * Builds the `editBenchmarkGrid` input from the drawer form. Mirrors
 * {@link buildCreateBenchmarkGridInput} but keys off the grid id and omits
 * fields the edit route does not accept (type, repCount).
 */
export const buildEditBenchmarkGridInput = (
  benchmarksForm: BenchmarksForm,
  gridId: string,
) => {
  if (benchmarksForm.type === 'kpi') {
    return {
      id: gridId,
      name: benchmarksForm.name,
      updateType: 'none' as const,
      coreExercises: [],
      metricIds: benchmarksForm.metricIds,
    };
  }
  return {
    id: gridId,
    name: benchmarksForm.name,
    updateType: benchmarksForm.updateType,
    coreExercises: mapCoreExercises(benchmarksForm.coreExerciseIds),
    metricIds: [],
  };
};
